// Build indices showing where Alerts touch the rest of the graph.
// Single source — Catalog cards, Rooms cards, RecommendedActions all
// read these so an alert surfaces consistently across every tab.

import { SAMPLE_PROJECT } from "../data/sampleGraph";
import type { GraphNode } from "../types/ontology";

interface AlertRef {
  alert: GraphNode;
  /** Edge kind that links the alert to the target (OF, RULE_FIRED_FROM, etc.) */
  via: string;
}

export interface AlertsIndex {
  /** All open alerts. */
  alerts: GraphNode[];
  /** SKU id → alerts that target it (Alert -[OF]-> SKU). */
  bySkuId: Map<string, AlertRef[]>;
  /** Room id → alerts that target SKUs placed in that room. */
  byRoomId: Map<string, AlertRef[]>;
  /** Rule id → alerts produced by it (Alert -[RULE_FIRED_FROM]-> Rule). */
  byRuleId: Map<string, AlertRef[]>;
  /** Milestone id → alerts blocking it (Alert -[OF]-> Milestone, or BLOCKED_BY). */
  byMilestoneId: Map<string, AlertRef[]>;
}

function build(): AlertsIndex {
  const alerts = SAMPLE_PROJECT.nodes.filter((n) => n.kind === "Alert");
  const bySku = new Map<string, AlertRef[]>();
  const byRoom = new Map<string, AlertRef[]>();
  const byRule = new Map<string, AlertRef[]>();
  const byMs = new Map<string, AlertRef[]>();

  const skuToRoom = new Map<string, string>();
  for (const e of SAMPLE_PROJECT.edges) {
    if (e.kind === "IN_ROOM") skuToRoom.set(e.source, e.target);
  }

  for (const alert of alerts) {
    for (const edge of SAMPLE_PROJECT.edges) {
      if (edge.source !== alert.id) continue;
      const tgt = SAMPLE_PROJECT.nodes.find((n) => n.id === edge.target);
      if (!tgt) continue;
      const ref: AlertRef = { alert, via: edge.kind };
      if (tgt.kind === "SKU") {
        push(bySku, tgt.id, ref);
        const roomId = skuToRoom.get(tgt.id);
        if (roomId) push(byRoom, roomId, ref);
      } else if (tgt.kind === "Milestone") {
        push(byMs, tgt.id, ref);
      } else if (tgt.kind === "Rule") {
        push(byRule, tgt.id, ref);
      } else if (tgt.kind === "Project") {
        // Project-level alerts cascade to every room.
        for (const r of SAMPLE_PROJECT.nodes.filter((n) => n.kind === "Room")) {
          push(byRoom, r.id, ref);
        }
      }
    }
  }

  return { alerts, bySkuId: bySku, byRoomId: byRoom, byRuleId: byRule, byMilestoneId: byMs };
}

function push<K, V>(m: Map<K, V[]>, k: K, v: V): void {
  const cur = m.get(k);
  if (cur) cur.push(v);
  else m.set(k, [v]);
}

// Memoised — sample project is static for the demo.
let _index: AlertsIndex | null = null;
export function alertsIndex(): AlertsIndex {
  if (!_index) _index = build();
  return _index;
}
